import React, { useState, useEffect, useRef } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  Card, 
  Button, 
  Space, 
  Tooltip,
  Empty,
  Spin,
  Radio,
  Typography,
  Tag,
  message
} from 'antd';
import { 
  CalendarOutlined,
  ExpandOutlined,
  CompressOutlined,
  FilePdfOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title } = Typography;

const AdminProjectsGantt = () => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(ViewMode.Month);
  const [exportingPdf, setExportingPdf] = useState(false);
  const ganttRef = useRef(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects`);
      const data = await response.json();
      
      if (data.success || Array.isArray(data)) {
        const projectList = data.success ? data.data : data;
        setProjects(projectList);
        
        const ganttTasks = projectList
          .filter(project => {
            if (!project.startDate || !project.endDate) return false;
            const start = new Date(project.startDate);
            const end = new Date(project.endDate);
            return !isNaN(start.getTime()) && !isNaN(end.getTime());
          })
          .map((project, index) => {
            const start = new Date(project.startDate);
            const end = new Date(project.endDate);
            const validEnd = end > start ? end : new Date(start.getTime() + 86400000);
            
            return {
              id: project._id,
              name: project.name,
              start: start,
              end: validEnd,
              progress: calculateProgress(project),
              type: 'project',
              isDisabled: true,
              styles: {
                backgroundColor: getProjectColor(project.status, index),
                progressColor: getProgressColor(project.status),
                progressSelectedColor: '#1890ff'
              },
              project: project
            };
          })
          .sort((a, b) => a.start - b.start);
        
        setTasks(ganttTasks);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      message.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'cancelled') return 0;
    
    const start = new Date(project.startDate);
    const end = new Date(project.endDate);
    const now = new Date();
    
    if (now < start) return 0;
    if (now > end) return 100;
    
    const total = end - start;
    const elapsed = now - start;
    return Math.round((elapsed / total) * 100);
  };

  const getProjectColor = (status, index) => {
    const colors = [
      '#1890ff', '#52c41a', '#722ed1', '#fa8c16', 
      '#13c2c2', '#eb2f96', '#2f54eb', '#faad14'
    ];
    
    if (status === 'completed') return '#52c41a';
    if (status === 'on-hold') return '#faad14';
    if (status === 'cancelled') return '#ff4d4f';
    
    return colors[index % colors.length];
  };

  const getProgressColor = (status) => {
    if (status === 'completed') return '#389e0d';
    if (status === 'on-hold') return '#d48806';
    if (status === 'cancelled') return '#cf1322';
    return '#096dd9';
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      active: { color: 'blue', text: 'Active' },
      completed: { color: 'green', text: 'Completed' },
      'on-hold': { color: 'orange', text: 'On Hold' },
      cancelled: { color: 'red', text: 'Cancelled' }
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleExportPdf = async () => {
    if (!ganttRef.current || tasks.length === 0) {
      message.warning('No timeline data to export');
      return;
    }

    try {
      setExportingPdf(true);
      message.loading({ content: 'Generating PDF...', key: 'pdfExport' });

      const ganttElement = ganttRef.current;
      
      const canvas = await html2canvas(ganttElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: ganttElement.scrollWidth,
        windowHeight: ganttElement.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = 297;
      const pdfHeight = 210;
      const margin = 10;
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2) - 20;

      const ratio = Math.min(availableWidth / (imgWidth / 3.78), availableHeight / (imgHeight / 3.78));
      const scaledWidth = (imgWidth / 3.78) * ratio;
      const scaledHeight = (imgHeight / 3.78) * ratio;

      pdf.setFontSize(16);
      pdf.setTextColor(33, 33, 33);
      pdf.text('All Projects Timeline Overview', margin, margin + 5);
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on: ${dayjs().format('MMMM DD, YYYY HH:mm')}`, margin, margin + 12);
      pdf.text(`Total Projects: ${tasks.length}`, pdfWidth - margin - 40, margin + 12);

      const xOffset = margin + (availableWidth - scaledWidth) / 2;
      const yOffset = margin + 18;

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);

      pdf.save(`All_Projects_Timeline_${dayjs().format('YYYY-MM-DD')}.pdf`);
      
      message.success({ content: 'PDF downloaded successfully!', key: 'pdfExport' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error({ content: 'Failed to generate PDF', key: 'pdfExport' });
    } finally {
      setExportingPdf(false);
    }
  };

  const TaskListHeader = ({ headerHeight, fontFamily, fontSize }) => {
    return (
      <div
        style={{
          height: headerHeight,
          fontFamily,
          fontSize,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          borderBottom: '1px solid #e8e8e8',
          background: '#fafafa',
          fontWeight: 600,
          minWidth: 280
        }}
      >
        <span style={{ flex: 1 }}>Project Name</span>
        <span style={{ width: 80, textAlign: 'center' }}>Status</span>
      </div>
    );
  };

  const TaskListTable = ({ tasks, rowHeight, fontFamily, fontSize }) => {
    return (
      <div style={{ fontFamily, fontSize }}>
        {tasks.map((task, index) => (
          <div
            key={task.id}
            style={{
              height: rowHeight,
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              borderBottom: '1px solid #f0f0f0',
              background: index % 2 === 0 ? '#fff' : '#fafafa',
              minWidth: 280
            }}
          >
            <span style={{ 
              flex: 1, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              paddingRight: 8,
              fontWeight: 500
            }}>
              📁 {task.name}
            </span>
            <span style={{ width: 80, textAlign: 'center' }}>
              {getStatusTag(task.project?.status)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const TooltipContent = ({ task }) => {
    const project = task.project;
    return (
      <div style={{ padding: '8px 12px', minWidth: 200 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{task.name}</div>
        <div style={{ fontSize: 12, color: '#666' }}>
          <div><strong>Start:</strong> {dayjs(task.start).format('MMM DD, YYYY')}</div>
          <div><strong>End:</strong> {dayjs(task.end).format('MMM DD, YYYY')}</div>
          <div><strong>Duration:</strong> {dayjs(task.end).diff(dayjs(task.start), 'day')} days</div>
          <div><strong>Progress:</strong> {task.progress}%</div>
          {project?.client && <div><strong>Client:</strong> {project.client}</div>}
          {project?.milestone && <div><strong>Milestone:</strong> {project.milestone}</div>}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  const projectsWithoutDates = projects.filter(p => !p.startDate || !p.endDate);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="mb-0">
          <CalendarOutlined className="mr-2" />
          Projects Timeline Overview
        </Title>
      </div>

      <Card>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-4">
            <span className="text-gray-600">
              Showing <strong>{tasks.length}</strong> projects with dates
            </span>
            {projectsWithoutDates.length > 0 && (
              <Tag color="warning">
                {projectsWithoutDates.length} projects without dates
              </Tag>
            )}
          </div>
          <Space>
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              size="small"
            >
              <Radio.Button value={ViewMode.Day}>Day</Radio.Button>
              <Radio.Button value={ViewMode.Week}>Week</Radio.Button>
              <Radio.Button value={ViewMode.Month}>Month</Radio.Button>
              <Radio.Button value={ViewMode.Year}>Year</Radio.Button>
            </Radio.Group>
            <Tooltip title="Refresh">
              <Button 
                icon={<ReloadOutlined />}
                onClick={fetchProjects}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Download PDF">
              <Button 
                icon={<FilePdfOutlined />}
                onClick={handleExportPdf}
                loading={exportingPdf}
                size="small"
                disabled={tasks.length === 0}
              >
                PDF
              </Button>
            </Tooltip>
          </Space>
        </div>

        {tasks.length === 0 ? (
          <Empty 
            description={
              <span>
                No projects with start and end dates found.
                <br />
                Please add dates to your projects to see them on the timeline.
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div ref={ganttRef} style={{ overflowX: 'auto', backgroundColor: '#fff' }}>
            <Gantt
              tasks={tasks}
              viewMode={viewMode}
              listCellWidth="280px"
              columnWidth={viewMode === ViewMode.Year ? 350 : viewMode === ViewMode.Month ? 300 : viewMode === ViewMode.Week ? 150 : 60}
              barCornerRadius={4}
              barFill={65}
              rowHeight={50}
              headerHeight={50}
              todayColor="rgba(24, 144, 255, 0.15)"
              TaskListHeader={TaskListHeader}
              TaskListTable={TaskListTable}
              TooltipContent={TooltipContent}
            />
          </div>
        )}

        {projectsWithoutDates.length > 0 && (
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <Title level={5} className="text-yellow-700 mb-2">
              Projects Without Dates ({projectsWithoutDates.length})
            </Title>
            <div className="flex flex-wrap gap-2">
              {projectsWithoutDates.map(project => (
                <Tag key={project._id} color="default">
                  {project.name}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminProjectsGantt;
